import { de_PutPearsonQuestionCommand, se_PutPearsonQuestionCommand, } from "../protocols/Aws_restJson1";
import { getSerdePlugin } from "@smithy/middleware-serde";
import { Command as $Command } from "@smithy/smithy-client";
export { $Command };
/**
 * Create a new person template question.
 * @example
 * Use a bare-bones client and the command you need to make an API call.
 * ```javascript
 * import { Apiv2Client, PutPearsonQuestionCommand } from "@codegrade/apiv2-client"; // ES Modules import
 * // const { Apiv2Client, PutPearsonQuestionCommand } = require("@codegrade/apiv2-client"); // CommonJS import
 * const client = new Apiv2Client(config);
 * const input = { // PutPearsonQuestionRequest
 *   tenantId: "STRING_VALUE", // required
 *   templateId: "STRING_VALUE", // required
 *   questionId: "STRING_VALUE", // required
 *   name: "STRING_VALUE", // required
 *   source: { // PutPearsonQuestionSource Union: only one key present
 *     coding: { // PutPearsonCodingQuestionSource
 *       assignmentId: Number("int"), // required
 *     },
 *     multipleChoice: { // PearsonMultipleChoiceQuestionData
 *       question: "STRING_VALUE", // required
 *       answers: [ // PearsonMultipleChoiceQuestionAnswers // required
 *         { // PearsonMultipleChoiceQuestionAnswer
 *           answer: "STRING_VALUE", // required
 *           isCorrect: true || false,
 *           hint: "STRING_VALUE",
 *         },
 *       ],
 *       answerOrder: "index-order" || "random-order",
 *     },
 *     selectAll: { // PearsonSelectAllQuestionData
 *       question: "STRING_VALUE", // required
 *       answers: [ // PearsonSelectAllQuestionAnswers // required
 *         { // PearsonSelectAllQuestionAnswer
 *           answer: "STRING_VALUE", // required
 *           isCorrect: true || false,
 *           hints: { // PearsonSelectAllQuestionAnswerHints
 *             correct: "STRING_VALUE",
 *             incorrect: "STRING_VALUE",
 *           },
 *         },
 *       ],
 *       answerOrder: "index-order" || "random-order",
 *     },
 *   },
 * };
 * const command = new PutPearsonQuestionCommand(input);
 * const response = await client.send(command);
 * // { // PutPearsonQuestionResponse
 * //   questionId: "STRING_VALUE", // required
 * // };
 *
 * ```
 *
 * @param PutPearsonQuestionCommandInput - {@link PutPearsonQuestionCommandInput}
 * @returns {@link PutPearsonQuestionCommandOutput}
 * @see {@link PutPearsonQuestionCommandInput} for command's `input` shape.
 * @see {@link PutPearsonQuestionCommandOutput} for command's `response` shape.
 * @see {@link Apiv2ClientResolvedConfig | config} for Apiv2Client's `config` shape.
 *
 * @throws {@link InternalServerError} (server fault)
 *  An internal failure at the fault of the server.
 *
 * @throws {@link BadRequestError} (client fault)
 *  An error at the fault of the client sending invalid input.
 *
 * @throws {@link NotFoundError} (client fault)
 *  An error due to the client attempting to access a missing
 * resource.
 *
 * @throws {@link NotAuthorizedError} (client fault)
 *  An error due to the client not being authorized to access the
 * resource.
 *
 * @throws {@link NotAuthenticatedError} (client fault)
 *  An error due to the client not being authenticated.
 *
 * @throws {@link ValidationError} (client fault)
 *  A standard error for input validation failures.
 * This should be thrown by services when a member of the input structure
 * falls outside of the modeled or documented constraints.
 *
 * @throws {@link Apiv2ServiceException}
 * <p>Base exception class for all service exceptions from Apiv2 service.</p>
 *
 *
 * @example An MCQ Question
 * ```javascript
 * //
 * const input = {
 *   name: "The name of the MCQ step",
 *   questionId: "question-id",
 *   source: {
 *     multipleChoice: {
 *       answerOrder: "index-order",
 *       answers: [
 *         {
 *           answer: "An option, text in markdown.",
 *           hint: "Read the answer to find out which one is correct"
 *         },
 *         {
 *           answer: "The correct answer, must be given exactly once.",
 *           isCorrect: true
 *         }
 *       ],
 *       question: "The question text in markdown"
 *     }
 *   },
 *   templateId: "template-id",
 *   tenantId: "tenant-id"
 * };
 * const command = new PutPearsonQuestionCommand(input);
 * const response = await client.send(command);
 * /* response is
 * { /* metadata only *\/ }
 * *\/
 * ```
 *
 * @example A Select All Question
 * ```javascript
 * //
 * const input = {
 *   name: "The name of the Select All Question step",
 *   questionId: "question-id",
 *   source: {
 *     selectAll: {
 *       answerOrder: "random-order",
 *       answers: [
 *         {
 *           answer: "Option 1, this one is correct",
 *           isCorrect: true
 *         },
 *         {
 *           answer: "Option 2, this one is wrong",
 *           isCorrect: false
 *         },
 *         {
 *           answer: "Option 3, this one is also correct",
 *           isCorrect: true
 *         }
 *       ],
 *       question: "The question text in markdown"
 *     }
 *   },
 *   templateId: "template-id",
 *   tenantId: "tenant-id"
 * };
 * const command = new PutPearsonQuestionCommand(input);
 * const response = await client.send(command);
 * /* response is
 * { /* metadata only *\/ }
 * *\/
 * ```
 *
 * @public
 */
export class PutPearsonQuestionCommand extends $Command.classBuilder()
    .m(function (Command, cs, config, o) {
    return [
        getSerdePlugin(config, this.serialize, this.deserialize),
    ];
})
    .s("Apiv2", "PutPearsonQuestion", {})
    .n("Apiv2Client", "PutPearsonQuestionCommand")
    .f(void 0, void 0)
    .ser(se_PutPearsonQuestionCommand)
    .de(de_PutPearsonQuestionCommand)
    .build() {
}
